import { Box, Grid2, Stack, Typography } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import type { Curation } from '@components/DrilldownHeader';
import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { IMAGING_TEST_TYPE_OPTIONS } from '@constants/option-types/imaging-test';
import * as ROUTES from '@constants/routes';

import {
  useCreateImagingDiagnosticReport,
  useUpdateImagingDiagnosticReport,
} from '@hooks/api/diagnostic-report';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';

import type {
  DiagnosticReportRecord,
  DiagnosticReportValues,
  FormValues,
} from '@models/diagnostic-report';
import { getDiagnosticReportTemplate } from '@models/fhir-templates/diagnostic-report';
import { getEncounterTemplate } from '@models/fhir-templates/encounter';
import { getLocationTemplate } from '@models/fhir-templates/location';
import type { DrilldownProps } from '@models/record-drilldown';

import { formatFacility } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import DrilldownWrapper from '../Common/DrilldownWrapper';
import DicomImageManager from './DicomImageManager';
import DicomImages from './DicomImages';

const messages = defineMessages({
  history: {
    id: 'imagingTestsHistory.history',
    defaultMessage: 'History',
  },
  technique: {
    id: 'imagingTestsHistory.technique',
    defaultMessage: 'Technique',
  },
  findings: {
    id: 'imagingTestsHistory.findings',
    defaultMessage: 'Findings',
  },
  impression: {
    id: 'imagingTestsHistory.impression',
    defaultMessage: 'Impression',
  },
  recommendations: {
    id: 'imagingTestsHistory.recommendations',
    defaultMessage: 'Recommendations',
  },
  images: {
    id: 'imagingTestsHistory.images',
    defaultMessage: 'Images',
  },
  test: {
    id: 'imagingTestsHistory.test',
    defaultMessage: 'Date Tested',
  },
  reported: {
    id: 'imagingTestsHistory.reported',
    defaultMessage: 'Date Reported',
  },
  typeName: {
    id: 'imagingTestsHistory.typeName',
    defaultMessage: 'Test Type',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  testName: {
    id: 'imagingTestsHistory.testName',
    defaultMessage: 'Test Name',
  },
  imagingTests: {
    id: 'imagingTestsHistory.imagingTests',
    defaultMessage: 'Imaging Tests',
  },
  imagesAvailable: {
    id: 'imagingTestsHistory.imagesAvailable',
    defaultMessage: 'Are Images Available?',
  },
  noImagesAvailable: {
    id: 'imagingTestsHistory.noImagesAvailable',
    defaultMessage: 'No images available',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
});

interface ImagingTestProps extends DrilldownProps<DiagnosticReportRecord> {
  canManageImgs?: boolean;
  canViewImgs?: boolean;
  /**
   * When true, suppresses the default redirect to the IMAGING_TESTS route
   * after successfully creating a new imaging test.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const ImagingTestsContent: React.FC<ImagingTestProps> = ({
  canManageImgs = false,
  canViewImgs = false,
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isMobile = useIsMobile('lg');
  const isModifying = isCreating || isEditing;

  const [createDiagnosticReport, createDiagnosticReportLoading] =
    useCreateImagingDiagnosticReport();
  const [updateDiagnosticReport, updateDiagnosticReportLoading] =
    useUpdateImagingDiagnosticReport();

  const loading =
    baseLoading ||
    updateDiagnosticReportLoading ||
    createDiagnosticReportLoading ||
    recordStatusAndSourceLoading;

  const testType = data?.typeName;
  const friendlyName = data?.testName
    ? `${testType ? `${testType} -` : ''}${data.testName}`
    : null;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.testName ?? '',
    city: data?.city ?? '',
    code: data?.code ?? '',
    country: data?.country ?? '',
    details: data?.details ?? '',
    facilityName: data?.facilityName ?? '',
    findings: data?.findings ?? '',
    history: data?.history ?? '',
    imagesAvailable: data?.imagesAvailable ?? true,
    practitioner: data?.practitioner ?? '',
    recommendations: data?.recommendations ?? '',
    reportedDate: data?.reportedDate ?? '',
    selfReported: data?.selfReported ?? false,
    technique: data?.technique ?? '',
    testDate: data?.testDate ?? '',
    typeName: data?.typeName ?? '',
    associatedResources: [],
  };

  const imageValidationSchema: Schema<
    Partial<Omit<DiagnosticReportValues, 'results'>>
  > = object().shape({
    title: string().required(formatMessage(messages.required)),
    city: string(),
    code: string(),
    country: string(),
    details: string(),
    facilityName: string(),
    findings: string(),
    history: string(),
    imagesAvailable: boolean(),
    practitioner: string(),
    recommendations: string(),
    reportedDate: string(),
    selfReported: boolean().required(formatMessage(messages.required)),
    technique: string(),
    testDate: string(),
    typeName: string(),
    associatedResources: array().of(
      string().required(formatMessage(messages.required))
    ),
  });
  const validationSchema: Schema<
    Partial<DiagnosticReportValues>,
    any,
    any,
    Flags
  > = imageValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) return;
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let imgTest: DiagnosticReportRecord | null | undefined;
    try {
      if (data?.id) {
        const { data: updatedImgTest } = await updateDiagnosticReport({
          input: {
            diagnostic_report_id: data.id,
            location: {
              id: data.encounter?.location?.id,
              ...getLocationTemplate({
                city: values.city,
                country: values.country,
                name: values.facilityName,
                region: null,
              }),
            },
            encounter: {
              id: data.encounter?.id,
              ...getEncounterTemplate(
                patient_id,
                {
                  practitioner: values.practitioner,
                  complications: null,
                  diagnosis: null,
                  endDate: null,
                  erNotes: null,
                  followUp: null,
                  selfReported: false,
                  startDate: null,
                  title: values.title ?? '',
                  visitDurationDays: null,
                  visitDurationUnits: null,
                },
                'imaging-and-tests'
              ),
            },
            diagnosticReport: data.getModelInput(values),
          },
        });
        imgTest = updatedImgTest;
      } else {
        const { data } = await createDiagnosticReport({
          input: {
            diagnosticReport: getDiagnosticReportTemplate(
              patient_id,
              values,
              'imaging-and-tests'
            ),
            encounter: getEncounterTemplate(
              patient_id,
              {
                practitioner: values.practitioner,
                complications: null,
                diagnosis: null,
                endDate: null,
                erNotes: null,
                followUp: null,
                selfReported: false,
                startDate: null,
                title: values.title ?? '',
                visitDurationDays: null,
                visitDurationUnits: null,
              },
              'imaging-and-tests'
            ),
            location: getLocationTemplate({
              city: values.city,
              country: values.country,
              name: values.facilityName,
              region: null,
            }),
          },
        });
        imgTest = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: imgTest?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (imgTest && isCreating && !disableNavigateOnCreate) {
        navigate(`${ROUTES.IMAGING_TESTS}${imgTest.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            {isModifying ? (
              <DrilldownItem
                headerLabel={formatMessage(messages.imagesAvailable)}
                label={formatMessage(
                  values.imagesAvailable ? messages.yes : messages.no
                )}
                name="imagesAvailable"
                type="checkbox"
                value={formatMessage(
                  data?.imagesAvailable ? messages.yes : messages.no
                )}
              />
            ) : data?.imagesAvailable ? null : (
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Icon name="image-slash" size="lg" />
                <Typography variant="subtitle1">
                  {formatMessage(messages.noImagesAvailable)}
                </Typography>
              </Box>
            )}

            {canManageImgs && data ? (
              <DicomImageManager diagnostic_report={data} />
            ) : null}
            {(canViewImgs || isSharing) && !data?.imagesAvailable ? null : (
              <DicomImages diagnostic_report={data} is_sharing={isSharing} />
            )}
            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12 }}>
                <DrilldownItem
                  label={formatMessage(messages.typeName)}
                  name="typeName"
                  onChange={(value) => setFieldValue('typeName', value)}
                  options={IMAGING_TEST_TYPE_OPTIONS}
                  type="dropdown"
                  value={data?.typeName}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DrilldownItem
                  label={formatMessage(messages.history)}
                  name="history"
                  type="text-area"
                  value={data?.history}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DrilldownItem
                  label={formatMessage(messages.technique)}
                  name="technique"
                  type="text-area"
                  value={data?.technique}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DrilldownItem
                  charLimit={10000}
                  label={formatMessage(messages.findings)}
                  name="findings"
                  type="text-area"
                  value={data?.findings}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <DrilldownItem
                  label={formatMessage(messages.impression)}
                  name="details"
                  type="text-area"
                  value={data?.details}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <DrilldownItem
                  label={formatMessage(messages.recommendations)}
                  name="recommendations"
                  type="text-area"
                  value={data?.recommendations}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <RelatedRecordsForm associations={values.associatedResources} />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <RecordAttachmentForm
                  isCreating={isCreating}
                  onFileChange={(files: DroppedFile[]) => {
                    setFieldValue('files', files);
                  }}
                  patientId={patient_id}
                  record={{
                    attachments: data?.attachments,
                    resourceId: data?.getResourceId(),
                  }}
                />
              </Grid2>
            </Grid2>

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );

        const drilldownProps: Curation.DrilldownHeaderProps = {
          items: [
            {
              editingComponent: (
                <FormikTextField
                  isHeaderItem
                  name="practitioner"
                  placeholder={formatMessage(messages.practitioner)}
                />
              ),
              title: data?.practitioner,
              iconName: 'user-doctor',
            },
            {
              editingComponent: (
                <>
                  <FormikTextField
                    isHeaderItem
                    name="facilityName"
                    placeholder={formatMessage(messages.facility)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="city"
                    placeholder={formatMessage(messages.city)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="country"
                    placeholder={formatMessage(messages.country)}
                  />
                </>
              ),
              title: formatFacility(
                data?.facilityName,
                data?.city,
                undefined,
                data?.country
              ),
              iconName: 'location-dot',
            },
          ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              columnGap={isMobile ? 0 : 2}
            >
              <FormikFhirDateField
                formats={['date', 'year']}
                label={formatMessage(messages.test)}
                name="testDate"
                onChange={(value) => {
                  setFieldValue('testDate', value);
                }}
                value={data?.testDate}
                max={true}
              />
              <FormikFhirDateField
                formats={['date', 'year']}
                label={formatMessage(messages.reported)}
                name="reportedDate"
                onChange={(value) => {
                  setFieldValue('reportedDate', value);
                }}
                value={data?.reportedDate}
                max={true}
              />
            </Stack>
          ) : (
            [
              formatDateLabel(messages.test, data?.getTestDateStr()),
              formatDateLabel(messages.reported, data?.getReportedDateStr()),
            ]
          ),
          title: friendlyName || formatMessage(messages.imagingTests),
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.testName)}
            />
          ),
        };

        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={ROUTES.IMAGING_TESTS}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ImagingTestsContent;
